import { useMemo, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  Button,
  Modal,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";

const tdLabelSx = {
  width: 300,
  bgcolor: "#f3f3f3",
  borderRight: "1px solid #e0e0e0",
  color: "#333",
  fontWeight: 600,
  verticalAlign: "top",
};

const tdFieldSx = { p: 1.5 };

const noteSx = {
  color: "#666",
  fontSize: 12,
  lineHeight: 1.25,
};

const modalBoxSx = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "#fff",
  p: { xs: 2, sm: 3 },
  borderRadius: 2,
  width: "min(760px, calc(100vw - 32px))",
  boxShadow: 24,
  outline: "none",
};

const normalizeCode = (value) => {
  const only = String(value || "").replace(/\D/g, "").slice(0, 3);
  return only ? only.padStart(3, "0") : "";
};

const emptyEstForm = {
  id: null,
  codigo: "",
  nombre: "",
  direccion: "",
  activo: true,
};

const normalizeEstablecimientos = (establecimientos, empresa) => {
  if (Array.isArray(establecimientos) && establecimientos.length > 0) {
    return establecimientos
      .map((e) => ({
        id: e.id || null,
        codigo: normalizeCode(e.codigo),
        nombre: String(e.nombre || "").trim(),
        direccion: String(e.direccion || "").trim(),
        activo: typeof e.activo === "boolean" ? e.activo : true,
      }))
      .filter((e) => e.codigo)
      .map((e) => ({
        ...e,
        nombre: e.nombre || `ESTABLECIMIENTO ${e.codigo}`,
      }));
  }

  const codigo = normalizeCode(empresa?.numero_establecimiento || "001");

  return [
    {
      id: null,
      codigo,
      nombre: `ESTABLECIMIENTO ${codigo}`,
      direccion: empresa?.direccion || "",
      activo: true,
    },
  ];
};

const EnterpriseForm = ({
  empresa,
  setEmpresa,
  handleChange,
  handleCheck,
  handleFile,
  fileLabel,
  openPreview,
  submit,
  logoRef,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [estModalOpen, setEstModalOpen] = useState(false);
  const [estForm, setEstForm] = useState(emptyEstForm);
  const [estEditIndex, setEstEditIndex] = useState(null);
  const [estError, setEstError] = useState("");

  const establecimientos = useMemo(() => {
    return normalizeEstablecimientos(empresa.establecimientos, empresa);
  }, [empresa]);

  const setEstablecimientos = (next) => {
    const normalized = normalizeEstablecimientos(next, empresa);

    setEmpresa((prev) => ({
      ...prev,
      establecimientos: normalized,
      numero_establecimiento: normalized.map((e) => e.codigo).join(", "),
    }));
  };

  const openCreateEst = () => {
    setEstEditIndex(null);
    setEstError("");
    setEstForm({
      ...emptyEstForm,
      direccion: empresa.direccion || "",
      activo: true,
    });
    setEstModalOpen(true);
  };

  const openEditEst = (item, index) => {
    setEstEditIndex(index);
    setEstError("");
    setEstForm({
      id: item.id || null,
      codigo: normalizeCode(item.codigo),
      nombre: item.nombre || "",
      direccion: item.direccion || "",
      activo: typeof item.activo === "boolean" ? item.activo : true,
    });
    setEstModalOpen(true);
  };

  const closeEstModal = () => {
    setEstModalOpen(false);
    setEstEditIndex(null);
    setEstError("");
    setEstForm(emptyEstForm);
  };

  const saveEstablecimiento = () => {
    const codigo = normalizeCode(estForm.codigo);
    const nombre = String(estForm.nombre || "").trim() || `ESTABLECIMIENTO ${codigo}`;
    const direccion = String(estForm.direccion || "").trim();
    const activo = !!estForm.activo;

    if (!codigo || codigo === "000") {
      setEstError("Ingrese un código de establecimiento válido.");
      return;
    }

    const exists = establecimientos.some((e, index) => {
      if (estEditIndex !== null && index === estEditIndex) return false;
      return normalizeCode(e.codigo) === codigo;
    });

    if (exists) {
      setEstError("Ya existe un establecimiento con ese código.");
      return;
    }

    const item = {
      id: estForm.id || null,
      codigo,
      nombre,
      direccion,
      activo,
    };

    if (estEditIndex !== null) {
      const next = establecimientos.map((e, index) => (index === estEditIndex ? item : e));
      setEstablecimientos(next);
    } else {
      setEstablecimientos([...establecimientos, item]);
    }

    closeEstModal();
  };

  const deleteEstablecimiento = (index) => {
    const next = establecimientos.filter((_, i) => i !== index);
    setEstablecimientos(next.length ? next : []);
  };

  const handleEstFormChange = (patch) => {
    setEstForm((prev) => ({
      ...prev,
      ...patch,
    }));
  };

  return (
    <Box component="form" onSubmit={submit}>
      <input
        type="hidden"
        name="establecimientos"
        value={JSON.stringify(establecimientos)}
        readOnly
      />

      <Box sx={{ bgcolor: "#f3f3f3", borderBottom: "1px solid #e0e0e0", p: 1.5 }}>
        <Typography sx={{ fontWeight: 700, color: "#333" }}>
          Configuraciones de la Empresa
        </Typography>
      </Box>

      <Table sx={{ "& td, & th": { borderBottom: "1px solid #eaeaea" } }}>
        <TableBody>
          <TableRow>
            <TableCell sx={tdLabelSx}>Ruc:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField fullWidth name="ruc" value={empresa.ruc || ""} onChange={handleChange} />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Razón social:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField
                fullWidth
                name="razon_social"
                value={empresa.razon_social || ""}
                onChange={handleChange}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Nombre comercial:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField
                fullWidth
                name="nombre_comercial"
                value={empresa.nombre_comercial || ""}
                onChange={handleChange}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>
              Establecimientos:
              <Box sx={noteSx}>
                Administra los establecimientos de la empresa. Ejemplo: 001 MAREA 1, 002 MAREA 2.
              </Box>
            </TableCell>
            <TableCell sx={tdFieldSx}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openCreateEst}
                    sx={{ textTransform: "none", bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" } }}
                  >
                    Añadir establecimiento
                  </Button>
                </Box>

                {establecimientos.length === 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: "#fafafa",
                      borderStyle: "dashed",
                    }}
                  >
                    <Typography sx={{ color: "#666", fontSize: 14 }}>
                      No hay establecimientos configurados.
                    </Typography>
                  </Paper>
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                      gap: 1.5,
                    }}
                  >
                    {establecimientos.map((e, index) => (
                      <Paper
                        key={`${e.codigo}-${index}`}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: e.activo ? "#fff" : "#f7f7f7",
                          opacity: e.activo ? 1 : 0.75,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                          <StorefrontOutlinedIcon color="primary" sx={{ mt: 0.25 }} />

                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 800, color: "#222", lineHeight: 1.15 }}>
                              {e.codigo} - {e.nombre}
                            </Typography>

                            <Typography sx={{ fontSize: 13, color: "#666", mt: 0.4 }}>
                              {e.direccion || "Sin dirección específica"}
                            </Typography>

                            <Typography
                              sx={{
                                display: "inline-flex",
                                mt: 1,
                                px: 1,
                                py: 0.25,
                                borderRadius: 99,
                                fontSize: 12,
                                fontWeight: 800,
                                bgcolor: e.activo ? "#e8f5e9" : "#eeeeee",
                                color: e.activo ? "#1b5e20" : "#666",
                              }}
                            >
                              {e.activo ? "Activo" : "Inactivo"}
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                            <Tooltip title="Editar establecimiento">
                              <IconButton size="small" onClick={() => openEditEst(e, index)}>
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Eliminar establecimiento">
                              <IconButton size="small" onClick={() => deleteEstablecimiento(index)}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Obligado a llevar contabilidad:</TableCell>
            <TableCell sx={tdFieldSx}>
              <FormControl fullWidth>
                <InputLabel>Selecciona</InputLabel>
                <Select
                  label="Selecciona"
                  name="obligado_contabilidad"
                  value={empresa.obligado_contabilidad || "NO"}
                  onChange={handleChange}
                >
                  <MenuItem value="NO">NO</MenuItem>
                  <MenuItem value="SI">SI</MenuItem>
                </Select>
              </FormControl>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Contribuyente especial:</TableCell>
            <TableCell sx={tdFieldSx}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!empresa.contribuyente_especial}
                    onChange={handleCheck}
                    name="contribuyente_especial"
                  />
                }
                label=""
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Gran Contribuyente:</TableCell>
            <TableCell sx={tdFieldSx}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!empresa.gran_contribuyente}
                    onChange={handleCheck}
                    name="gran_contribuyente"
                  />
                }
                label=""
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Exportador:</TableCell>
            <TableCell sx={tdFieldSx}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!empresa.exportador}
                    onChange={handleCheck}
                    name="exportador"
                  />
                }
                label=""
              />

              {empresa.exportador && (
                <RadioGroup
                  row
                  name="exportador_tipo"
                  value={empresa.exportador_tipo || "NO_HABITUAL"}
                  onChange={handleChange}
                  sx={{ ml: 2 }}
                >
                  <FormControlLabel value="NO_HABITUAL" control={<Radio />} label="No habitual" />
                  <FormControlLabel value="HABITUAL" control={<Radio />} label="Habitual" />
                </RadioGroup>
              )}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Contribuyente Régimen RIMPE:</TableCell>
            <TableCell sx={tdFieldSx}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!empresa.contribuyente_rimpe}
                    onChange={handleCheck}
                    name="contribuyente_rimpe"
                  />
                }
                label=""
              />

              {empresa.contribuyente_rimpe && (
                <FormControl sx={{ ml: 2, minWidth: 340 }}>
                  <InputLabel>Seleccione</InputLabel>
                  <Select
                    label="Seleccione"
                    name="regimen_tipo"
                    value={empresa.regimen_tipo || empresa.rimpe_tipo || ""}
                    onChange={handleChange}
                  >
                    <MenuItem value="RIMPE_EMPRENDEDOR">Contribuyente Régimen RIMPE</MenuItem>
                    <MenuItem value="RIMPE_NEGOCIO_POPULAR">
                      Contribuyente Negocio Popular - Régimen RIMPE
                    </MenuItem>
                  </Select>
                </FormControl>
              )}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Contribuyente Régimen GENERAL:</TableCell>
            <TableCell sx={tdFieldSx}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!empresa.regimen_general || empresa.regimen_tipo === "GENERAL"}
                    onChange={handleCheck}
                    name="regimen_general"
                  />
                }
                label=""
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Agente de retención:</TableCell>
            <TableCell sx={tdFieldSx}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!empresa.agente_retencion}
                    onChange={handleCheck}
                    name="agente_retencion"
                  />
                }
                label=""
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Ciudad:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField
                fullWidth
                name="ciudad"
                value={empresa.ciudad || ""}
                onChange={handleChange}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Teléfonos:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField
                fullWidth
                name="telefonos"
                value={empresa.telefonos || ""}
                onChange={handleChange}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Dirección:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField
                fullWidth
                multiline
                minRows={1}
                name="direccion"
                value={empresa.direccion || ""}
                onChange={handleChange}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Email Notificación:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField
                fullWidth
                type="email"
                name="email_notificacion"
                value={empresa.email_notificacion || ""}
                onChange={handleChange}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>
              Logo de la Empresa:
              <Box sx={noteSx}>(Ancho y alto máximo: 500x300 px)</Box>
              <Box sx={noteSx}>Formato permitido: jpg, jpeg, png.</Box>
            </TableCell>
            <TableCell sx={tdFieldSx}>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  onClick={() => logoRef.current?.click()}
                  startIcon={<FolderOpenIcon />}
                  sx={{ textTransform: "none", bgcolor: "#1976d2" }}
                >
                  Seleccionar
                </Button>

                <input
                  ref={logoRef}
                  type="file"
                  name="logo_empresa"
                  accept="image/png,image/jpeg"
                  hidden
                  onChange={handleFile}
                />

                <Box sx={{ color: "#777" }}>{fileLabel(empresa.logo_empresa)}</Box>

                <Box sx={{ flexGrow: 1 }} />

                {!!empresa.logo_empresa && (
                  <Button
                    variant="contained"
                    onClick={() => openPreview(empresa.logo_empresa)}
                    startIcon={<SearchIcon sx={{ color: "#fff" }} />}
                    sx={{
                      textTransform: "none",
                      bgcolor: "#0AA1FF",
                      color: "#fff",
                      "&:hover": { bgcolor: "#0a8be6" },
                    }}
                  >
                    Ver
                  </Button>
                )}
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Box sx={{ p: 2, textAlign: "right", borderTop: "1px solid #eaeaea" }}>
        <Button
          type="submit"
          variant="contained"
          sx={{ textTransform: "none", bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" } }}
        >
          Guardar Cambios
        </Button>
      </Box>

      <Modal open={estModalOpen} onClose={closeEstModal}>
        <Box sx={modalBoxSx}>
          <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 0.5 }}>
            {estEditIndex !== null ? "Editar establecimiento" : "Añadir establecimiento"}
          </Typography>

          <Typography sx={{ color: "#666", fontSize: 13, mb: 2 }}>
            El código debe ser de tres dígitos. El nombre te ayuda a identificarlo en facturación.
          </Typography>

          {estError && (
            <Paper
              variant="outlined"
              sx={{
                p: 1.25,
                mb: 2,
                bgcolor: "#fff3f3",
                borderColor: "#ffcdd2",
              }}
            >
              <Typography sx={{ color: "#b71c1c", fontWeight: 700, fontSize: 13 }}>
                {estError}
              </Typography>
            </Paper>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 2fr",
              gap: 2,
            }}
          >
            <TextField
              fullWidth
              label="Código"
              value={estForm.codigo}
              placeholder="001"
              onChange={(e) =>
                handleEstFormChange({
                  codigo: e.target.value.replace(/\D/g, "").slice(0, 3),
                })
              }
            />

            <TextField
              fullWidth
              label="Nombre"
              value={estForm.nombre}
              placeholder="MAREA 1"
              onChange={(e) =>
                handleEstFormChange({
                  nombre: e.target.value,
                })
              }
            />

            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Dirección"
              value={estForm.direccion}
              onChange={(e) =>
                handleEstFormChange({
                  direccion: e.target.value,
                })
              }
              sx={{ gridColumn: isMobile ? "auto" : "1 / -1" }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={!!estForm.activo}
                  onChange={(e) =>
                    handleEstFormChange({
                      activo: e.target.checked,
                    })
                  }
                />
              }
              label="Establecimiento activo"
              sx={{ gridColumn: isMobile ? "auto" : "1 / -1" }}
            />
          </Box>

          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: "#f8fafc",
              borderRadius: 2,
            }}
          >
            <Typography sx={{ fontSize: 13, color: "#555", fontWeight: 700 }}>
              Vista previa
            </Typography>
            <Typography sx={{ fontSize: 15, color: "#111", fontWeight: 800, mt: 0.5 }}>
              {normalizeCode(estForm.codigo) || "000"} -{" "}
              {estForm.nombre || `ESTABLECIMIENTO ${normalizeCode(estForm.codigo) || "000"}`}
            </Typography>
          </Paper>

          <Divider sx={{ mt: 2 }} />

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 2 }}>
            <Button onClick={closeEstModal}>Cancelar</Button>
            <Button variant="contained" onClick={saveEstablecimiento}>
              Guardar
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default EnterpriseForm;